import { ContentLibRest, TContentLibVideosSearchRequest } from '../../common/rest/contentLib/contentLibRest';
import { ContentLibActions } from '../store/contentLib/actions';
import { getDispatch, getState } from '../store';
import { ContentLibSelectors } from '../store/contentLib/selectors';
import {
  createGroupNameIndex, findLanguageByCode,
  findLanguageById,
  getUserGroupById,
  getUserGroupByVideoId,
  getVideoById
} from '../store/models/selectors';
import { UserGroupEffects } from './userGroupEffects';
import { fetchUserGroup } from '../../common/rest/userGroup/fetchUserGroup';
import { addGroup, addVideoInGroup } from '../store/models/actions';
import { updateUserGroups } from './updateUserGroups';
import { TUserGroup } from '../store/models/types';
import { EContentLibVideoValidType, IContentLibVideo } from '../store/contentLib/types';
import { YoutubeUrlUtil } from '../../common/utils/youtube-url-util';
import { VideoMetaInfo } from '../../common/utils/videoMetaInfo';
import { LangUtil } from '../../common/utils/lang-util';

export class ContentLibEffects {

  public static async searchGroups() {
    const dispatch = getDispatch();
    const state = getState();
    const {targetLangCode} = ContentLibSelectors.getSearchFilter(state);
    if (!targetLangCode) {
      return;
    }
    dispatch(ContentLibActions.setGroupsSearchResult({ loading: true }));
    try {
      const groups = await ContentLibRest.searchGroups({
        targetLangCode,
        searchText: '',
        maxVideoCount: 100
      });
      dispatch(ContentLibActions.setGroupsSearchResult({ groups, loading: false }));
    } catch(e) {
      console.error(e)
      dispatch(ContentLibActions.setGroupsSearchResult({ groups: [], loading: false }));
    }
  }

  public static async searchVideos(filter: TContentLibVideosSearchRequest): Promise<IContentLibVideo[]> {
    if (!filter.targetLangCode) {
      return [];
    }
    return ContentLibRest.searchVideos(filter);
  }

  public static async copyVideo(contentLibGroupId: number, contentLibVideoId: number, targetGroupId: number) {
    const state = getState();
    const dispatch = getDispatch();
    const contentLibGroup = ContentLibSelectors.findGroupById(state, contentLibGroupId);
    const targetGroup = getUserGroupById(state, targetGroupId);
    if (!targetGroup) {
      const targetLangCode = findLanguageById(state, contentLibGroup?.targetLangId || 0)?.code || '';
      const nativeLangCode = findLanguageById(state, contentLibGroup?.nativeLangId || 0)?.code || '';
      let groupName = createGroupNameIndex(state, contentLibGroup?.title || '');
      targetGroupId = await UserGroupEffects.createGroup(groupName, targetLangCode, nativeLangCode);
      const createdGroup = await dispatch(fetchUserGroup(targetGroupId));
      dispatch(addGroup(createdGroup));
    }
    const video = await ContentLibRest.copyVideo(contentLibVideoId, targetGroupId) as any;
    dispatch(addVideoInGroup(video, targetGroupId));
    return {videoId: video.videoKey, groupId: targetGroupId}
  }

  public static async checkVideoExistLang(video: IContentLibVideo, langCode: string): Promise<boolean> {
    const portugalLangCodes = ['pt', 'ptbr'];
    const validCaptionsCodes = (portugalLangCodes.includes(langCode)) ? portugalLangCodes : [langCode];
    const captionLangs = await this.getVideoCaptionLangs(video);
    const checkCaptionLangs = captionLangs.map(code => LangUtil.checkLangCode(code));
    const videoCaptionLangExist = checkCaptionLangs.findIndex(code => validCaptionsCodes.includes(code)) >= 0;

    if (!video.subtitleLangs?.length || !videoCaptionLangExist) {
      const validType: EContentLibVideoValidType = videoCaptionLangExist ? EContentLibVideoValidType.VALID : EContentLibVideoValidType.NO_SUBTITLE_AUDIO_LANG;
      await this.updateVideo(video.id, {
        subtitleLangs: captionLangs.join(','),
        validType
      })
    }

    return videoCaptionLangExist;
  }

  private static async getVideoCaptionLangs(video: IContentLibVideo): Promise<string[]> {
    if (!!video.subtitleLangs?.length) {
      return video.subtitleLangs.split(',').map(code => LangUtil.checkLangCode(code.trim()));
    }
    const langCaptions = await VideoMetaInfo.downloadLangFromServiceWorker(video.url);
    if (langCaptions) {
      const langs = langCaptions.map(l => l.code);
    /*  if (video.langId) {
        const videoLang = findLanguageById(getState(), video.langId)
        if (videoLang) {
          const exist = langs.map(lcode => LangUtil.checkLangCode(lcode)).find(lcode => lcode === videoLang.code)
        }
      }*/
    /*
      await this.updateVideo(video.id, {
        subtitleLangs: langs.join(',')
      })*/
      return langs;
    }
    return [];
  }

  public static async previewVideo(contentLibGroupId: number, contentLibVideoId: number, videoUrl: string) {
    const state = getState();
    const dispatch = getDispatch();
    const videoKey = YoutubeUrlUtil.getVideoId(videoUrl);
    if (videoKey) {
      const video = getVideoById(state, videoKey);
      if (video) {
        const group = getUserGroupByVideoId(state, videoKey);
        return {videoId: video.videoKey, groupId: group?.id};
      }
    }

    //const contentLibGroup = ContentLibSelectors.findGroupById(state, contentLibGroupId);
    //const targetLangCode = findLanguageById(state, contentLibGroup?.langId || 0)?.code || '';
    const {video, groupId} = await ContentLibRest.previewVideo(contentLibVideoId);
    const group = getUserGroupById(state, groupId);
    if (!group) {
      await dispatch(updateUserGroups(true));
    } else {
      dispatch(addVideoInGroup(video, groupId));
    }
    return {videoId: video.videoKey, groupId}
  }

  public static async copyGroup(contentLibGroupId: number, updateGroups: boolean = true): Promise<TUserGroup> {
    const dispatch = getDispatch();
    const group: TUserGroup = await ContentLibRest.copyGroup(contentLibGroupId);
    if (updateGroups) {
      await dispatch(updateUserGroups(true));
    }
    return group;
  }

  public static async loadLangs() {
    const dispatch = getDispatch();
    const langs = await ContentLibRest.getLangList();
    dispatch(ContentLibActions.setLangs(langs));
  }

  public static async loadCategories() {
    const dispatch = getDispatch();
    const categories = await ContentLibRest.getCategoryList();
    dispatch(ContentLibActions.setCategories(categories));
  }

  public static async updateVideo(id: number, updateData: Partial<Omit<IContentLibVideo, 'id'>>) {
    const video = await ContentLibRest.updateVideo(id, updateData);
    const dispatch = getDispatch();
    dispatch(ContentLibActions.updateVideo(id, video));
  }
}